import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Box, Grid2, Paper, Skeleton, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import TopList from "../../components/TopList/top-list.component";
import HomeOfficePolicyReminder from "../../components/home-office-policy.component";
import { isMobileWorkingEnabled } from "../../functions/isMobileWorkingEnabled.function";
import { useRemoteFetchMetadata } from "../../hooks/Remote/Metadata/useRemoteFetchMetadata";
import { useRemoteFetchUserProfile } from "../../hooks/Remote/User/useRemoteFetchUserProfile";
import { useRemoteFetchUserReport } from "../../hooks/Remote/User/useRemoteFetchUserReport";
import { useRemoteUserHomeOfficeInfo } from "../../hooks/Remote/User/useRemoteUserHomeOfficeInfo";
import { useElementInView } from "../../hooks/useElementInView/useElementInView";
import { LocationUsage } from "../Reports/typings/reports.types";
import Schedule from "../Schedule/schedule.component";
import {
  dashboardCardSx,
  topListGridParentSx,
  topListKpiGridItemSx,
  topListPaperSx
} from "./dashboard.styles";
import { GoToManagementLink } from "../Management/components/GoToManagementLink";
import { DashboardTopListCarousel } from "./components/DashboardTopListCarousel";
import { useElementWidth } from "../Dashboard2/hooks/useElementWidth";
import {
  TOP_LIST_TWO_COLUMN_MIN_WIDTH_PX,
  shouldUseDashboardTopListCarousel
} from "./utils/dashboard-top-list-carousel.utils";

/**
 * @description The dashboard component renders the schedule for the week and top locations.
 * @version 0.1.0
 */

export type UserLocationDetails = {
  duration: LocationUsage[];
  firstName: string;
  surname: string;
  id: number;
};

export type HomeOfficeRatio = {
  bookingNormHours: number;
  maxMobileWorkingBookingHours: number;
  minMobileWorkingBookingHours: number;
  minWeeklyBookingHours: number;
  mobileWorkingBookingHours: number;
  userId: string;
};

/** Stable keys for workplace top-list loading skeleton rows (not derived from map index). */
const WORKPLACE_TOP_LIST_SKELETON_KEYS = [
  "workplace-top-list-skeleton-row-1",
  "workplace-top-list-skeleton-row-2",
  "workplace-top-list-skeleton-row-3"
] as const;

const Dashboard = () => {
  const {
    userInformation: {
      company: {
        meta: { workplaceBookingEnabled, showBookingStatistics, bookingAssistenceActive }
      }
    }
  } = useSelector((state: RootState) => state.login);

  const theme = useTheme();
  const dashboardCardStyles = useMemo(() => dashboardCardSx(theme), [theme]);

  const { data: metadata } = useRemoteFetchMetadata();
  const { data: userProfile } = useRemoteFetchUserProfile();

  const mobileWorkingEnabled: boolean = useMemo(
    () => isMobileWorkingEnabled(metadata, userProfile),
    [metadata, userProfile]
  );

  const { data: userHomeOfficeInfo, isLoading: userHomeOfficeInfoLoading } =
    useRemoteUserHomeOfficeInfo();
  const { ref: workplaceTopListInViewRef, isInView: isWorkplaceTopListInView } = useElementInView();
  const [topListWidthRef, topListWidthPx] = useElementWidth();
  const setWorkplaceTopListSectionRef = useCallback(
    (node: HTMLElement | null) => {
      workplaceTopListInViewRef(node);
      topListWidthRef(node);
    },
    [topListWidthRef, workplaceTopListInViewRef]
  );
  const [startDate, endDate] = useMemo(
    () => [
      DateTime.now().minus({ month: 1 }).toFormat("yyyy-MM-dd"),
      DateTime.now().toFormat("yyyy-MM-dd")
    ],
    []
  );
  const shouldFetchUserReport = workplaceBookingEnabled && isWorkplaceTopListInView;
  const { data: userReport, isLoading: userReportLoading } = useRemoteFetchUserReport(
    {
      startDate,
      endDate
    },
    shouldFetchUserReport
  );

  const [isQuickAutomatedPlaceBooking, setIsQuickAutomatedPlaceBooking] = useState(false);

  // Keep skeleton until the section is in view and top-list data is ready.
  const isLoading =
    workplaceBookingEnabled &&
    (!isWorkplaceTopListInView || userHomeOfficeInfoLoading || userReportLoading);
  const visibleUserReportDurations =
    userReport?.[0]?.duration?.filter(
      usage => usage.locationName !== "No Booking Time" || showBookingStatistics
    ) ?? [];
  const locationCount = visibleUserReportDurations.length;
  const [useTopListCarousel, setUseTopListCarousel] = useState(true);
  useLayoutEffect(() => {
    setUseTopListCarousel(wasUsingCarousel =>
      shouldUseDashboardTopListCarousel({
        containerWidthPx: topListWidthPx,
        locationCount,
        wasUsingCarousel
      })
    );
  }, [topListWidthPx, locationCount]);
  const isNarrowTopList = topListWidthPx < TOP_LIST_TWO_COLUMN_MIN_WIDTH_PX;
  const showWorkplaceTopList = workplaceBookingEnabled;
  const showHomeOfficeReminder =
    userHomeOfficeInfo?.[0]?.minMobileWorkingBookingHours !== undefined &&
    (userHomeOfficeInfo?.[0]?.minMobileWorkingBookingHours ?? 0) > 0;

  const renderWorkplaceTopListSection = () => {
    if (!showWorkplaceTopList) return null;

    if (isLoading) {
      return (
        <Grid2 ref={setWorkplaceTopListSectionRef} size={{ xs: 12, md: 4, lg: 3 }}>
          <Paper variant="outlined" sx={dashboardCardStyles} data-testid="dashboard-kpi-skeleton">
            <Skeleton variant="text" width={240} />
            {WORKPLACE_TOP_LIST_SKELETON_KEYS.map(rowKey => (
              <Box
                key={rowKey}
                display="flex"
                width={"100%"}
                justifyContent={"row"}
                sx={{ margin: 0.5 }}
              >
                <Skeleton sx={{ mr: 1 }} variant="rectangular" width={"20%"} height={40} />
                <Skeleton width={"81%"} variant="text" />
              </Box>
            ))}
          </Paper>
        </Grid2>
      );
    }

    return (
      <Grid2
        ref={setWorkplaceTopListSectionRef}
        size={{ xs: 12 }}
        sx={{ width: "100%" }}
        data-testid="top-list-grid-parent"
      >
        {useTopListCarousel ? (
          <DashboardTopListCarousel
            locations={visibleUserReportDurations}
            mobileWorkingEnabled={mobileWorkingEnabled}
            bookingAssistenceActive={bookingAssistenceActive}
            setIsQuickAutomatedPlaceBooking={setIsQuickAutomatedPlaceBooking}
          />
        ) : (
          <Box sx={topListGridParentSx(isNarrowTopList)}>
            {visibleUserReportDurations.map(usage => (
              <Box
                key={`${usage.locationInventoryId}-${usage.locationName}`}
                sx={topListKpiGridItemSx}
                data-testid="top-list-grid-item"
              >
                <Paper variant="outlined" sx={topListPaperSx(theme)}>
                  <TopList
                    value={usage}
                    setIsQuickAutomatedPlaceBooking={setIsQuickAutomatedPlaceBooking}
                    displayBookNowButton={
                      (!usage.isMobileWorking || mobileWorkingEnabled) && bookingAssistenceActive
                    }
                    bookButtonColor="secondary"
                    bookButtonVariant="contained"
                  />
                </Paper>
              </Box>
            ))}
          </Box>
        )}
      </Grid2>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 1
        }}
      >
        <GoToManagementLink data-testid="dashboard-management-link" />
      </Box>

      <Grid2 container direction={"row"} justifyContent={"flex-start"} spacing={1}>
        {renderWorkplaceTopListSection()}

        {showHomeOfficeReminder && (
          <Grid2
            size={{ xs: 12, sm: 6, md: 4, lg: 2.5 }}
            data-testid="minMobileWorkingBookingHours-grid"
          >
            <Paper variant="outlined" sx={dashboardCardStyles}>
              <HomeOfficePolicyReminder homeOfficeRatio={userHomeOfficeInfo[0]} />
            </Paper>
          </Grid2>
        )}
      </Grid2>

      <Schedule
        isQuickAutomatedPlaceBooking={isQuickAutomatedPlaceBooking}
        setIsQuickAutomatedPlaceBooking={setIsQuickAutomatedPlaceBooking}
      />
    </>
  );
};

export default Dashboard;
